/**
 * Sparkline — minimal SVG line chart for KPI cards.
 * No axes, no labels, no tooltip. Just the trend.
 */

import { cn } from '@/lib/utils';

export interface SparklineProps {
  values: number[];
  color?: string;
  width?: number;
  height?: number;
  strokeWidth?: number;
  className?: string;
  /** Fill area under line */
  fill?: boolean;
}

export function Sparkline({
  values, color = 'currentColor', width = 80, height = 24,
  strokeWidth = 1.5, className, fill = false,
}: SparklineProps) {
  if (!values || values.length < 2) return null;

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;

  const stepX = width / (values.length - 1);
  const points = values.map((v, i) => ({
    x: i * stepX,
    y: height - ((v - min) / range) * (height - strokeWidth) - strokeWidth / 2,
  }));

  const path = points.reduce((acc, p, i) =>
    acc + (i === 0 ? `M ${p.x} ${p.y}` : ` L ${p.x} ${p.y}`),
    '',
  );

  const fillPath = fill
    ? `${path} L ${width} ${height} L 0 ${height} Z`
    : null;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={cn('overflow-visible', className)}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      {fillPath && (
        <path d={fillPath} fill={color} fillOpacity={0.12} />
      )}
      <path
        d={path}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
